"use client"

import React, { useCallback, useState } from 'react'
import { Input } from '@/components/ui/input'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { VoiceInputButton, type VoiceInputState } from './voice-input-button'
import { useFieldVoice } from './use-field-voice'
import { useVoiceAvailable } from './use-voice-available'

const TEXT_TYPES = new Set([undefined, 'text', 'search', 'url', 'tel', 'email'])

export interface VoiceInputProps extends React.ComponentProps<typeof Input> {
  /**
   * Явное применение нового значения (основной путь). Обязателен там, где значение
   * сохраняется не через `onChange` (onBlur-сохранение, debounce, хук).
   */
  onVoiceInsert?: (next: string) => void
  /** Классы обёртки (`relative`): сюда переносятся ширина/flex, если они были на Input */
  wrapperClassName?: string
  onVoiceStateChange?: (state: VoiceInputState) => void
  /** Принудительно скрыть микрофон */
  noVoice?: boolean
  /** Классы оверлея записи (повторить радиус/фон поля, если они отличаются от базового Input) */
  voiceOverlayClassName?: string
  /** Классы idle-кнопки микрофона (позиция по умолчанию — справа по центру) */
  voiceButtonClassName?: string
  /** Дополнительные классы Input при показанном микрофоне (например, больший правый отступ) */
  voiceInputClassName?: string
  /** Вид кнопки: по умолчанию `ghost` (только иконка) — для узких desktop-полей; `filled` — круглая с фоном */
  voiceButtonVariant?: 'filled' | 'ghost'
  /**
   * Поисковое поле: при непустом значении слева от микрофона показывается крестик очистки,
   * вызывающий этот колбэк. Рендерится и без голосового ввода.
   */
  onClear?: () => void
}

/**
 * Drop-in замена `Input` с кнопкой микрофона справа. Если голосовой ввод недоступен
 * (флаг, браузер, нетекстовый type, disabled/readOnly) — рендерит ровно обычный `Input`.
 */
export const VoiceInput = React.forwardRef<HTMLInputElement, VoiceInputProps>(function VoiceInput(
  { onVoiceInsert, wrapperClassName, onVoiceStateChange, noVoice, voiceOverlayClassName, voiceButtonClassName, voiceInputClassName, voiceButtonVariant = 'ghost', onClear, className, type, disabled, readOnly, onBlur, onSelect, onKeyUp, onClick, ...props },
  ref,
) {
  const available = useVoiceAvailable()
  const showVoice = available && !noVoice && !disabled && !readOnly && TEXT_TYPES.has(type as string | undefined)

  const [voiceState, setVoiceState] = useState<VoiceInputState>('idle')
  const { fieldRef, insert, bindSelectionTracking } = useFieldVoice<HTMLInputElement>({ onVoiceInsert, uncontrolled: props.value === undefined, singleLine: true })

  const setRefs = useCallback((el: HTMLInputElement | null) => {
    fieldRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) ref.current = el
  }, [fieldRef, ref])

  const handleStateChange = useCallback((s: VoiceInputState) => {
    setVoiceState(s)
    onVoiceStateChange?.(s)
  }, [onVoiceStateChange])

  const showClear = !!onClear && !disabled && !readOnly && typeof props.value === 'string' && props.value.length > 0
  const clearButton = showClear ? (
    <button
      type="button"
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { onClear?.(); fieldRef.current?.focus() }}
      aria-label="Очистить"
      title="Очистить"
      className="col-start-1 row-start-1 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center justify-self-end rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  ) : null

  if (!showVoice) {
    if (!onClear) {
      return (
        <Input
          ref={setRefs}
          type={type}
          disabled={disabled}
          readOnly={readOnly}
          className={className}
          onBlur={onBlur}
          onSelect={onSelect}
          onKeyUp={onKeyUp}
          onClick={onClick}
          {...props}
        />
      )
    }
    return (
      <div className={cn('grid', wrapperClassName)}>
        <Input
          ref={setRefs}
          type={type}
          disabled={disabled}
          readOnly={readOnly}
          className={cn('col-start-1 row-start-1', className, showClear && 'pr-8')}
          onBlur={onBlur}
          onSelect={onSelect}
          onKeyUp={onKeyUp}
          onClick={onClick}
          {...props}
        />
        {clearButton && React.cloneElement(clearButton, { className: cn(clearButton.props.className, 'mr-1.5') })}
      </div>
    )
  }

  return (
    // В idle обёртка — grid без position: иначе позиционированная обёртка перекрывала бы
    // абсолютные иконки соседей (например, лупу поиска перед полем). Кнопка лежит в той же
    // ячейке, что и Input, и прижата вправо через justify-self. Пилюля записи — absolute,
    // поэтому на время записи обёртка становится relative.
    <div
      className={cn('grid', voiceState !== 'idle' && 'relative', wrapperClassName)}
      data-voice-error={voiceState === 'error' ? '' : undefined}
    >
      <Input
        ref={setRefs}
        type={type}
        disabled={disabled}
        readOnly={readOnly}
        className={cn('col-start-1 row-start-1', className, showClear ? 'pr-16' : 'pr-9', voiceInputClassName)}
        {...bindSelectionTracking({ onBlur, onSelect, onKeyUp, onClick })}
        {...props}
      />
      {/* Крестик очистки — левее микрофона; в момент записи скрыт под пилюлей-оверлеем */}
      {clearButton && voiceState === 'idle' && React.cloneElement(clearButton, {
        className: cn(clearButton.props.className, voiceButtonVariant === 'ghost' ? 'mr-9' : 'mr-8'),
      })}
      <VoiceInputButton
        size="sm"
        placement="overlay"
        variant={voiceButtonVariant}
        onInsert={insert}
        onStateChange={handleStateChange}
        className={cn(
          'col-start-1 row-start-1 self-center justify-self-end',
          voiceButtonVariant === 'ghost' ? 'mr-2' : 'mr-0.5',
          voiceButtonClassName,
        )}
        overlayClassName={voiceOverlayClassName}
      />
    </div>
  )
})
